import { router } from "@inertiajs/react"
import { Phone, Plus } from "@untitledui/icons"
import { useState } from "react"

import { BEFORE_YOU_DIAL } from "@/components/calls/copy"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { InputBase, TextField } from "@/components/ui/input"
import { TextArea } from "@/components/ui/textarea"
import { adminSchoolCallsPath } from "@/pages/admin/calls/paths"
import type { Attempt, StartCallPayload } from "@/pages/admin/calls/types"

import type { AgendaSection } from "../types"

/*
 * Before you dial: an inline expansion of the card block, not a new page. The
 * agenda freezes when the call starts, so this is the only moment to shape
 * it — untick a section you will not raise (it leaves the frozen agenda and
 * the scoring), add a one-line item of your own (stored as `asks:custom:n`,
 * scored like any ask), and leave a note to self that primes the transcriber
 * and the summariser. The dial number and attempt count restate the card line
 * so you confirm the target, not just the intent.
 */
export function BeforeYouDial({
  schoolId,
  schoolName,
  dialLabel,
  stafferMobile,
  attempt,
  agenda,
  onBack,
}: {
  schoolId: number
  schoolName: string
  dialLabel: string | null
  stafferMobile: string | null
  attempt: Attempt
  agenda: AgendaSection[]
  onBack: () => void
}) {
  const [ticked, setTicked] = useState<Set<string>>(
    () => new Set(agenda.map((s) => s.id)),
  )
  const [custom, setCustom] = useState<string[]>([])
  const [draft, setDraft] = useState("")
  const [note, setNote] = useState("")
  const [processing, setProcessing] = useState(false)

  const toggle = (id: string, on: boolean) =>
    setTicked((prev) => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  const add = () => {
    const line = draft.trim()
    if (!line) return
    setCustom((prev) => [...prev, line])
    setDraft("")
  }

  const callNow = () => {
    if (processing) return
    const payload: StartCallPayload = {
      section_ids: agenda.map((s) => s.id).filter((id) => ticked.has(id)),
      custom_items: custom,
      note_to_self: note.trim(),
    }
    setProcessing(true)
    router.post(
      adminSchoolCallsPath(schoolId),
      { call: { ...payload } },
      { preserveScroll: true, onFinish: () => setProcessing(false) },
    )
  }

  const detail = (s: AgendaSection) =>
    s.lines[0] ??
    (s.items.length > 1
      ? `${s.items.length} items`
      : (s.items[0]?.note ?? undefined))

  return (
    <section
      aria-label={`Before you dial ${schoolName}`}
      className="card flex flex-col gap-4 px-5 py-4"
    >
      <div className="border-secondary flex flex-col gap-0.5 border-b pb-2.5">
        <h2 className="text-primary text-md">Call {schoolName}</h2>
        <span className="notation">
          {dialLabel ?? "no number"} · attempt {attempt.step} of {attempt.of}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <span className="eyebrow">{BEFORE_YOU_DIAL.agendaHead}</span>
        <ul className="divide-tertiary flex flex-col divide-y">
          {agenda.map((s) => (
            <li key={s.id} className="flex min-h-11 items-center py-1.5">
              <Checkbox
                isSelected={ticked.has(s.id)}
                onChange={(on: boolean) => toggle(s.id, on)}
                label={s.label}
                hint={detail(s)}
              />
            </li>
          ))}
          {custom.map((line, i) => (
            <li
              key={`custom-${i}`}
              className="flex min-h-11 items-center justify-between gap-3 py-1.5"
            >
              <Checkbox
                isSelected
                onChange={(on: boolean) => {
                  if (!on) setCustom((prev) => prev.filter((_, j) => j !== i))
                }}
                label={line}
                hint={BEFORE_YOU_DIAL.addedTag}
              />
            </li>
          ))}
          <li className="py-2">
            <div className="provisional-slot flex items-center gap-2 px-3 py-2">
              <TextField
                aria-label={BEFORE_YOU_DIAL.addItem}
                value={draft}
                onChange={(v: string) => setDraft(v)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    add()
                  }
                }}
                className="min-w-0 flex-1"
              >
                <InputBase size="md" placeholder={BEFORE_YOU_DIAL.addItem} />
              </TextField>
              <Button
                size="md"
                color="secondary"
                iconLeading={Plus}
                onPress={add}
                isDisabled={!draft.trim()}
              >
                Add
              </Button>
            </div>
          </li>
        </ul>
      </div>

      <TextArea
        label={BEFORE_YOU_DIAL.noteHead}
        rows={2}
        placeholder="Karratha — spell it out. Jane's colleague may join."
        value={note}
        onChange={(v: string) => setNote(v)}
        hint={BEFORE_YOU_DIAL.noteHint}
      />

      <div className="border-secondary flex flex-col gap-3 border-t pt-3">
        <p className="text-secondary text-sm">{BEFORE_YOU_DIAL.instruction}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="lg"
            color="primary"
            iconLeading={Phone}
            onPress={callNow}
            isLoading={processing}
            isDisabled={!dialLabel}
            className="pointer-coarse:h-11"
          >
            Call now
          </Button>
          <Button size="lg" color="secondary" onPress={onBack}>
            Back
          </Button>
          {stafferMobile && (
            <span className="notation ml-auto">rings {stafferMobile}</span>
          )}
        </div>
      </div>
    </section>
  )
}
